/**
 * steps/AccountTypeStep.tsx
 * Account type selection step for the onboarding wizard
 * 
 * Handles account type selection with expanded options including custom types.
 * Uses HeroUI RadioGroup for type selection and Input for custom type names.
 * Integrates with draft persistence system and follows wizard step pattern.
 */

// 1. External dependencies
import React, { useState, useEffect } from 'react';
import { User, Building, FileText, Briefcase, Shield, Users, PiggyBank, Settings, Info } from 'lucide-react';

// 2. Internal absolute imports
import { RadioGroup, Radio, Input, Card, CardBody, CardHeader, CardFooter, Accordion, AccordionItem } from '@enfomentis/ui';
import { ACCOUNT_TYPE_OPTIONS } from '@/constants/accountType';
import type { AccountType } from '@/types/shared/AccountType';

// 3. Relative imports
import { useOnboardingData } from '../hooks/useOnboardingData';

/**
 * Map icon names to Lucide components
 */
const ICON_MAP = {
  'User': User,
  'Building': Building,
  'FileText': FileText,
  'Briefcase': Briefcase,
  'Shield': Shield,
  'Users': Users,
  'PiggyBank': PiggyBank,
  'Settings': Settings
};

/**
 * Account type selection step component
 */
export const AccountTypeStep: React.FC = () => {
  const { draft, updateAccount } = useOnboardingData();
  
  // Local form state
  const [formData, setFormData] = useState({
    accountType: 'personal' as AccountType,
    customAccountTypeName: ''
  });

  // Initialize from draft
  useEffect(() => {
    if (draft?.account) {
      setFormData({
        accountType: draft.account.accountType || 'personal',
        customAccountTypeName: draft.account.customAccountTypeName || ''
      });
    }
  }, [draft]);

  /**
   * Handle account type changes
   */
  const handleAccountTypeChange = (value: string): void => {
    const newAccountType = value as AccountType;
    const newFormData = { 
      ...formData, 
      accountType: newAccountType,
      // Clear custom name if switching away from 'other'
      customAccountTypeName: newAccountType !== 'other' ? '' : formData.customAccountTypeName
    };
    setFormData(newFormData);
    
    // Update draft immediately
    const updateData: { accountType: AccountType; customAccountTypeName?: string } = { 
      accountType: newAccountType 
    };
    if (newAccountType === 'other' && newFormData.customAccountTypeName) {
      updateData.customAccountTypeName = newFormData.customAccountTypeName;
    }
    updateAccount(updateData);
  };

  /**
   * Handle custom account type name changes
   */
  const handleCustomNameChange = (value: string): void => {
    const newFormData = { ...formData, customAccountTypeName: value };
    setFormData(newFormData);
    
    // Update draft immediately
    updateAccount({
      accountType: formData.accountType,
      customAccountTypeName: value
    });
  };

  /**
   * Check if form is valid for navigation
   */
  const isFormValid = (): boolean => {
    // If "other" is selected, custom name must be provided
    if (formData.accountType === 'other') {
      return !!formData.customAccountTypeName && formData.customAccountTypeName.trim().length > 0;
    }
    return !!formData.accountType;
  };

  return (
    <Card
      radius="none"
      shadow="none"
      className="h-full max-h-[100dvh] w-full flex flex-col border-0">
      <CardHeader>
        <div>
          <h3 className="text-large font-semibold text-text-primary">Account Type</h3>
          <p className="text-sm text-text-secondary">How do you trade? Select the type that best describes your trading setup.</p>
        </div>
      </CardHeader>
      <CardBody className="space-y-6">
        <div className="max-w-2xl mx-auto px-2">
          {/* Account Type Selection */}
          <div className="space-y-6">
            <RadioGroup
              value={formData.accountType}
              onValueChange={handleAccountTypeChange}
              orientation="vertical"
              size="lg"
              classNames={{
                wrapper: "space-y-3"
              }}
            >
              {ACCOUNT_TYPE_OPTIONS.map((option) => {
                const IconComponent = ICON_MAP[option.icon];
                const isSelected = formData.accountType === option.value;
                return (
                  <Card
                    key={option.value}
                    isPressable
                    isHoverable
                    className={`cursor-pointer transition-all duration-200 ${
                      isSelected
                        ? 'border-primary bg-primary-50 shadow-md'
                        : 'border-border-light hover:border-border-medium hover:shadow-sm'
                    }`}
                    onPress={() => handleAccountTypeChange(option.value)}
                  >
                    <CardBody className="p-4">
                      <div className="flex items-start gap-4">
                        <Radio
                          value={option.value}
                          size="lg"
                          classNames={{
                            base: "m-0 p-0"
                          }}
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <IconComponent className={`w-5 h-5 ${isSelected ? 'text-primary' : 'text-text-secondary'}`} />
                            <span className={`font-medium ${isSelected ? 'text-primary' : 'text-text-primary'}`}>
                              {option.label}
                            </span>
                          </div>
                          <p className="text-sm text-text-secondary leading-relaxed">
                            {option.description}
                          </p>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                );
              })}
            </RadioGroup>

            {/* Custom Account Type Input */}
            {formData.accountType === 'other' && (
              <div className="pl-8">
                <Input
                  label="Custom Account Type"
                  placeholder="e.g., Family Office, Hedge Fund, etc."
                  value={formData.customAccountTypeName}
                  onValueChange={handleCustomNameChange}
                  isRequired
                  variant="bordered"
                  size="lg"
                  description="Specify your custom account type"
                  errorMessage={
                    formData.customAccountTypeName.length > 0 && formData.customAccountTypeName.trim().length === 0
                      ? "Custom account type name is required"
                      : undefined
                  }
                  isInvalid={formData.customAccountTypeName.length > 0 && formData.customAccountTypeName.trim().length === 0}
                />
              </div>
            )}

            {/* Validation Status */}
            {isFormValid() && (
              <div className="flex items-center gap-2 text-success text-sm">
                <div className="w-1.5 h-1.5 rounded-full bg-success"></div>
                Account type selected! Ready to continue.
              </div>
            )}
          </div>
        </div>
      </CardBody>

      <CardFooter className="flex-shrink-0 border-t border-default-200">
        <Accordion variant="light" fullWidth isCompact>
          <AccordionItem
            textValue="Why do we need this?"
            key="why-needed"
            title={
              <div className="flex items-center gap-2">
                <Info size={16} className="text-text-secondary" />
                <span className="text-sm font-medium text-text-secondary">
                  Why do we need this?
                </span>
              </div>
            }
          >
            <ul className="text-sm text-text-secondary space-y-1 pb-2">
              <li>• Your account type helps us show relevant brokers and features</li>
              <li>• Different account types have different trading rules and requirements</li>
              <li>• This helps us provide more personalized guidance throughout setup</li>
            </ul>
          </AccordionItem>
        </Accordion>
      </CardFooter>
    </Card>
  );
};